'use client'

import BodyText from '@lyra/core/components/BodyText'
import Icon, { ArrowUpRight } from '@lyra/core/components/Icon'
import Table, { createColumnHelper } from '@lyra/core/components/Table'
import filterNulls from '@lyra/core/utils/filterNulls'
import formatNumber from '@lyra/core/utils/formatNumber'
import formatTruncatedAddress from '@lyra/core/utils/formatTruncatedAddress'
import formatUSD from '@lyra/core/utils/formatUSD'
import { PageId } from '@lyra/web/constants/pages'
import { RewardsRoundLeaderboardUser } from '@lyra/web/constants/rewards'
import { TokenId } from '@lyra/web/constants/tokens'
import { getPagePath } from '@lyra/web/utils/pages'
import { useMemo } from 'react'
import { StackProps } from 'tamagui'

type Props = {
  leaderboard: RewardsRoundLeaderboardUser[]
} & StackProps

const LeaderboardTable = ({ leaderboard, ...tableProps }: Props) => {
  const rows = useMemo(() => {
    const sortedLeaderboard = [...leaderboard].sort((a, b) => a.feesRank - b.feesRank)
    return sortedLeaderboard.map((row) => ({
      ...row,
      onPress: () =>
        window.open(getPagePath({ page: PageId.User, address: row.address }), '_blank'),
    }))
  }, [leaderboard])

  const columnHelper = createColumnHelper<RewardsRoundLeaderboardUser>()

  const columns = useMemo(
    () =>
      filterNulls([
        columnHelper.accessor('rank', {
          id: 'rank',
          header: 'Rank',
          meta: {
            minWidth: 40,
            maxWidth: 40,
          },
          cell: (props) => {
            const { feesRank } = props.row.original
            return <BodyText>{feesRank}</BodyText>
          },
        }),
        columnHelper.accessor('address', {
          id: 'address',
          header: 'User',
          meta: {
            minWidth: 100,
          },
          cell: (props) => {
            const { address, username } = props.row.original
            return <BodyText>{username ? username : formatTruncatedAddress(address)}</BodyText>
          },
        }),
        columnHelper.accessor('volume', {
          id: 'volume',
          header: 'Volume',
          meta: {
            minWidth: 100,
          },
          cell: (props) => {
            const { volume } = props.row.original
            return <BodyText>{formatUSD(volume)}</BodyText>
          },
        }),
        columnHelper.accessor('opRewards', {
          id: 'address',
          header: 'OP Rewards',
          meta: {
            minWidth: 90,
          },
          cell: (props) => {
            const { rewards } = props.row.original
            const opRewards = rewards[TokenId.OP] ?? 0
            return (
              <BodyText color={opRewards ? 'red' : 'secondary'}>
                {opRewards ? `${formatNumber(opRewards, { dps: 2 })} OP` : '—'}
              </BodyText>
            )
          },
        }),
        columnHelper.accessor('points', {
          id: 'address',
          header: 'stDRV Rewards',
          meta: {
            minWidth: 90,
          },
          cell: (props) => {
            const { rewards } = props.row.original
            const drvRewards = rewards[TokenId.STDRV] ?? 0
            return (
              <BodyText color={drvRewards ? 'cta' : 'secondary'}>
                {drvRewards ? formatNumber(drvRewards) + ' stDRV' : '—'}
              </BodyText>
            )
          },
        }),
        columnHelper.accessor('link', {
          id: 'link',
          header: '',
          meta: {
            minWidth: 15,
            maxWidth: 15,
          },
          cell: () => {
            return <Icon color="secondary" icon={<ArrowUpRight />} size={15} />
          },
        }),
      ]),
    [columnHelper]
  )

  return (
    <Table
      size="lg"
      data={rows}
      columns={columns}
      emptyMessage="No users"
      pageSize={100}
      {...tableProps}
    />
  )
}

export default LeaderboardTable
